import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import { cloneDeep, has, keys, uniqueId, isEqual } from "lodash";
import { store } from "../redux/store";
import * as deedPollUtils from "./deed-poll-utils";
import { resetFormData as resetStartFormData } from "../redux/slices/application-start-slice";
import { resetFormData as resetStep1FormData } from "../redux/slices/application-step-1-slice";
import { resetFormData as resetStep2FormData } from "../redux/slices/application-step-2-slice";
import { resetFormData as resetStep3FormData } from "../redux/slices/application-step-3-slice";
import { resetFormData as resetStep4FormData } from "../redux/slices/application-step-4-slice";
import { resetFormData as resetStep5FormData } from "../redux/slices/application-step-5-slice";
import {
  resetAuthData,
  resetRetrieveFromMyInfo,
  resetSingPassAuthData,
} from "../redux/slices/authentication-slice";
import { resetStageA } from "../redux/slices/completed-steps-slice";
import moment from "moment";
import { InstrumentTypeEnum } from "../models/enum/instrument-type.enum";
import { MyInfo } from "../models/polymer/MyInfo.model";
import { SexEnum } from "../models/enum/sex.enum";
import { convertRaceToEthnicCode } from "./form-helper";
import { PolymerAxios } from "./axios";
import { API_Application, API_EligibilityDebugLog } from "./api";
import { FileItem } from "../models/polymer/Document.model";

export class Helpers {
  static clearPersistData = async () => {
    return new Promise(async (resolve, reject) => {
      store.dispatch(resetStageA());
      store.dispatch(resetStartFormData());
      store.dispatch(resetStep1FormData());
      store.dispatch(resetStep2FormData());
      store.dispatch(resetStep3FormData());
      store.dispatch(resetStep4FormData());
      store.dispatch(resetStep5FormData());
      store.dispatch(resetRetrieveFromMyInfo(false));
      storage.removeItem("persist:app").then((res) => {
        console.log("---> CLEAR APPLICATION FORM DATA <----");
        resolve(true);
      });
    });
  };

  static clearAuthPersistData = () => {
    store.dispatch(resetAuthData());
    storage.removeItem("persist:auth").then((res) => {
      console.log("---> CLEAR AUTH DATA <----");
    });
  };
  static clearSingpassAuthPersistData = () => {
    store.dispatch(resetSingPassAuthData());
    storage.removeItem("persist:auth").then((res) => {
      console.log("---> CLEAR AUTH DATA <----");
    });
  };

  static isTodayAfterGivenDate(dateString: string) {
    if (!dateString) return false;

    const givenDate = moment(dateString, moment.ISO_8601, true);
    if (!givenDate.isValid()) {
      console.error("Invalid date string:", dateString);
      return false;
    }

    return moment().startOf("day").isAfter(givenDate.startOf("day"));
  }

  static formatDate(
    dateString?: string,
    monthType?: "short" | "long",
    includeTime: boolean = false
  ) {
    let monthFormatType = monthType ?? "long";
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);

    if (dateString.includes(" ") || includeTime) {
      let parts = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: monthFormatType,
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
        .formatToParts(date)
        .reduce((acc: any, part) => {
          acc[part.type] = part.value;
          return acc;
        }, {});

      return `${parts.day} ${parts.month} ${parts.year}, ${parts.hour}:${
        parts.minute
      }${parts.dayPeriod.toUpperCase()}`;
    }

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: monthFormatType,
      year: "numeric",
    }).format(date);
  }

  static getDayOfWeek = (dateStr: string) => {
    const date = new Date(dateStr); // Parse the date string

    // Format to get only the full day of the week (e.g., "Wednesday")
    const dayOfWeek = date.toLocaleDateString("en-GB", {
      weekday: "long", // Full day of the week
    });

    return dayOfWeek;
  };

  static formatTime(timeString?: string) {
    if (!timeString) {
      return "";
    }

    const formattedTimeString = moment(timeString, ["HH:mm:ss"]).format(
      "hh:mma"
    );
    return formattedTimeString;
  }

  /**
   * Stage E appointment slots are stored as a 1-hour range (e.g. "11:00 AM - 12:00 PM").
   * Citizens should only see the start of the slot.
   */
  static formatAppointmentStartTime(appointmentTime?: string) {
    if (!appointmentTime) {
      return "";
    }

    const startTime = appointmentTime.split(/\s*[-~]\s*/)[0]?.trim();
    return startTime || appointmentTime.trim();
  }

  static formatGetDayLeft(dateString: string) {
    if (!dateString) {
      return 0;
    }
    let eventdate = moment(dateString);
    let todaysdate = moment();
    return eventdate.diff(todaysdate, "days");
  }
  static formatGetDayAgo(dateString: string) {
    const daysLeft = this.formatGetDayLeft(dateString);
    if (daysLeft < 0) {
      const daysAgo = Math.abs(daysLeft);
      return daysAgo;
    } else {
      return 0;
    }
  }

  static formatCurrencyInput(e: string, isBlur?: boolean): string {
    let currency = ""; //Remove dollar sign
    if (e.indexOf(".") >= 0) {
      let decimal_pos = e.indexOf(".");
      let left_side = e.substring(0, decimal_pos);
      let right_side = e.substring(decimal_pos);

      // add commas to left side of number
      left_side = left_side
        .replace(/[^\d]+/g, "")
        .replace(/[^\d](?=(\d{3})+(?!\d))/g, "$1,");
      if (!left_side) {
        left_side = "0";
      }
      // validate right side
      right_side = right_side
        .replace(/[^\d]+/g, "")
        .replace(/[^\d](?=(\d{3})+(?!\d))/g, "$1,");

      // On blur make sure 2 numbers after decimal
      if (isBlur) {
        right_side += "00";
      }

      right_side = right_side.substring(0, 2);
      return currency + left_side + "." + right_side;
    } else {
      let input_val = e
        .replace(/[^\d]+/g, "")
        .replace(/[^\d](?=(\d{3})+(?!\d))/g, "$1,");
      if (isBlur) {
        if (input_val) {
          //When have value, eg: 1, then add .00 to become 1.00
          input_val += ".00";
        } else {
          //When no value, eg: empty, then we don't add .00, else it will become 'SGD .00'
          return "";
        }
      }
      return currency + input_val;
    }
  }

  static generateAlphaNumericID() {
    return uniqueId();
  }

  static formatThousandSeparator(amount?: string | number, unit?: string) {
    if (amount === undefined || amount === null) {
      return "";
    }
    let value_unit = unit ?? "";
    if (Number(amount) >= 1000) {
      return amount
        ? value_unit +
            amount
              .toString()
              .replace(/\B(?=(?:\d{3})+(?!\d))/g, ",")
              .replace(/(\.\d{4})\d*$/, "$1")
        : value_unit + amount;
    } else {
      return value_unit + amount;
    }
  }

  static formatStringToNumber(value?: string) {
    if (!value) {
      return 0;
    }
    if (typeof value === "number") {
      return value;
    } else {
      return parseFloat(value.replace(",", ""));
    }
  }

  static flattenNestedObj(object: any): any {
    return keys(object).reduce((result: any, fields: any, next) => {
      const value = object[fields.toString()];
      if (has(value, "isValid")) {
        return { ...result, [fields.toString()]: value };
      } else {
        const testing: any = this.flattenNestedObj(value);
        return { ...result, ...testing };
      }
    }, {});
  }

  static formatDocumentListTitle(
    title: string = "",
    owner_name: string = "",
    address: string = ""
  ) {
    let tempTitle = title;
    if (tempTitle.includes("<<Name>>")) {
      tempTitle = tempTitle.replace("<<Name>>", owner_name);
    } else if (tempTitle.includes("<<Address>>")) {
      tempTitle = tempTitle.replace("<<Address>>", address);
    }
    return tempTitle;
  }

  static formatBytesToMB(size: number) {
    let toFixedValue = 2;
    if (size <= 9999) {
      //if greater thn 9999 bytes, to change the tofixed value.
      toFixedValue = 3;
    }
    return (size / (1024 * 1024)).toFixed(toFixedValue);
  }

  /**
   *
   * @param size number in the bytes
   * @param exceedLimit number in the bytes
   * @returns
   */
  static isBytesExceed(current_bytes: number, exceed_bytes: number): boolean {
    return current_bytes > exceed_bytes; //is size is greater to 5mb = 5000000=bytes
  }

    /**
   * Strips the metadata prefix (e.g., "data:image/png;base64,") from a base64 string.
   * If no comma is found, returns the original string.
   * If input is undefined/null, returns an empty string.
   */
  static stripBase64(base64?: string) {
    if (!base64) return "";
    return base64.includes(",") ? base64.split(",")[1] : base64;
  }

 
  static appendTimestamp(fileName: string) {
    const nameSegments: string[] = fileName.split(".");
    const prefix = "_" + new Date().getTime().toString();
    
    if (nameSegments.length == 1) {
      return nameSegments[0] + prefix;
    }

    const secondLastIndex = nameSegments.length - 2;
    nameSegments[secondLastIndex] += prefix;
    return nameSegments.join(".");
  }

  static parseTimestamp(timestamp: string) {
    const [datePart, timePart] = timestamp.split(' ');
    const [day, month, year] = datePart.split('/');
    const fullYear = `20${year}`;
    return new Date(`${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}:00`);
  }

  static stripMimeType(mime_type?: string) {
    if (mime_type) {
      let subString = mime_type.split("/");
      return subString[1];
    } else {
      return mime_type;
    }
  }

  static maskContent(string?: string, enable: boolean = true) {
    if (enable) {
      return "***********";
    } else {
      return string;
    }
  }

  static maskPartialContent(string: string, enable: boolean = true) {
    if (!string) return "";

    if (enable) {
      const charactersLeft = 4;
      let lastFourChar = string.substring(
        string.length - charactersLeft,
        string.length
      );
      const maskedLength = string.length - charactersLeft;
      let resultString = "*".repeat(maskedLength) + lastFourChar;
      return resultString;
    } else {
      return string;
    }
  }

  static getYearFromDate = (date?: string) => {
    if (date) {
      let regexPattern = /\d{4}-\d{2}-\d{2}$/;

      if (regexPattern.test(date)) {
        let dateArr = date.split("-");
        return dateArr[0];
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  static amountToTwoDecimals = (value: string | number | undefined) => {
    if (value === undefined || value === 0) {
      return "$0.00";
    }

    if (typeof value === "number") {
      return Helpers.formatThousandSeparator(value.toFixed(2), "$ ");
    }

    const amount = Helpers.formatThousandSeparator(value, "$ ");
    if (amount.includes(".")) {
      if (
        amount.substring(amount.indexOf(".") + 1, amount.length).length === 2
      ) {
        return amount;
      } else {
        return amount + "0";
      }
    } else {
      return amount + ".00";
    }
  };

  static replaceUndefinedWithEmptyString(obj: any) {
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] === undefined) {
        obj[key] = "";
      }
    }
  }

  static checkIsValueChanged = (value: any, sliceValue: any) => {
    const tempValue = cloneDeep(value);
    const tempSliceValues = cloneDeep(sliceValue);
    this.replaceUndefinedWithEmptyString(tempValue);
    this.replaceUndefinedWithEmptyString(tempSliceValues);
    return !isEqual(tempValue, tempSliceValues);
  };

  static replaceEmptyStringWithNull = (obj: any) => {
    for (const key in obj) {
      if (
        obj.hasOwnProperty(key) &&
        typeof obj[key] === "string" &&
        obj[key] === ""
      ) {
        obj[key] = null;
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        this.replaceEmptyStringWithNull(obj[key]);
      }
    }
  };

  static formatTitleCase = (str: String) => {
    if (str) {
      return str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
  };

  static mergeMyInfoData = (oldMyInfo: MyInfo | undefined, newMyInfo: MyInfo): MyInfo => {
    //NOTE: Bind only all myinfo but not mobile_no and email, because mobile_no and email will be separate retrieve.
    //NOTE: when sex is unknown, do not need to bind;
  
    const { name, residential_status, dob, marital_status, sex, race, nationality, uinfin } = newMyInfo;
    
    // Convert dob from DD-MM-YYYY to YYYY-MM-DD if it exists and matches the pattern
    let formattedDob = dob;
    if (dob && typeof dob === 'string' && dob.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const [day, month, year] = dob.split('-');
      formattedDob = `${year}-${month}-${day}`;
    }
    
    const myInfo = new MyInfo({
      ...oldMyInfo,
      ...(!!name && { name }),
      ...(!!residential_status && { residential_status }),
      ...(!!dob && { dob: formattedDob }),  // Use the formatted date
      //...(!!dob && { dob }), 
      ...(!!marital_status && { marital_status }),
      ...(!!sex && sex !== SexEnum.unknown && { sex }),
      ...(!!race && { race: convertRaceToEthnicCode(race) }),
      ...(!!nationality && { nationality }),
      ...(!!uinfin && { uinfin }),
    })
  
    return myInfo;
  }

  /**
   * Logs errors to backend in a non-blocking way using the EligibilityDebugLog API
   * @param context - Context where the error occurred (e.g., "StagesFlow_SMS_Error", "Root_processNewApplication_RDB_Error")
   * @param errorData - The error data object with additional context
   * @param uinfin - User's UINFIN (optional)
   * @param singpassToken - SingPass token for authentication (optional)
   */
  static logErrorToBackend = async (
    context: string, 
    errorData: any, 
    uinfin?: string, 
    singpassToken?: string
  ): Promise<void> => {
    // Don't block user experience - run logging in background
    setTimeout(async () => {
      try {
        // Only attempt to log if we have authentication tokens
        if (uinfin && singpassToken) {
          const payload = {
            application_uuid: errorData?.applicationId || errorData?.application_id || uinfin,
            message: JSON.stringify({
              uinfin: uinfin,
              error_message: context,
              generic_error_message: "Frontend Interface Data Error",
              api_url: window?.location?.pathname || '',
              timestamp: new Date().toISOString(),
              userAgent: navigator?.userAgent || '',
              url: window?.location?.href || '',
              error_context: context,
              error_data: errorData,
              error: errorData ? JSON.stringify(errorData) : "cannot find error trace"
            })
          };

          await PolymerAxios(uinfin, singpassToken).post(API_EligibilityDebugLog, payload);
        } else {
          // Fallback to console logging if no auth tokens
          console.error(`[${context}] Error logged:`, {
            context,
            errorData,
            timestamp: new Date().toISOString(),
            url: window?.location?.href || ''
          });
        }
      } catch (loggingError) {
        // Silently fail - don't let logging errors affect user experience
        console.error('[ErrorLogging] Failed to log error to backend:', loggingError);
      }
    }, 0);
  };

  // Created purpose is the new flow of Document handling in A4
  static deleteDocumentsFromDB = async (fileItems: FileItem[], uinfin: string, singpassToken: string, applicationId : string) => {
    
    if (!fileItems || fileItems.length === 0) return;

    const client = PolymerAxios(uinfin ?? '', singpassToken ?? '');
    
    await Promise.all(
      fileItems.map(async (file) => {
        try {
          await client.post(
            `${API_Application}/${applicationId}/documents/${file.file_id}/deletion-request`
          );
          console.log(`Deleted ${file.file_id} successfully`);
        } catch (err) {
          console.error(`Failed to delete ${file.file_id}`, err);
        }
      })
    );
  };

  // Re-export deed poll functions from standalone utility (for easier imports)
  static parseNameWithPinyin = deedPollUtils.parseNameWithPinyin;
  static namesAreEqual = deedPollUtils.namesAreEqual;
  static checkDeedPollNameMatch = deedPollUtils.checkDeedPollNameMatch;
}
